import { employeeApi, type AttachmentRef, type UploadTicket } from "./employee-api";

export type { AttachmentRef, UploadTicket };

/**
 * Attach a file to a request from the browser (FEAT-2026-027), the same two
 * steps as the mobile app (BR-100, BR-101): ask the API for a short-lived
 * upload ticket, then PUT the bytes straight to storage with the headers the
 * ticket lists. The API never receives the file; the request then carries the
 * ticket's `key`.
 *
 * Checked here first so an unsupported file is refused before any upload; the
 * server re-checks size and type when issuing the ticket (BR-102).
 */
export const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/heic", "application/pdf"];
export const MAX_BYTES = 10 * 1024 * 1024;

/** An error to show, or null when the file can be uploaded. */
export function checkFile(file: File): string | null {
  // Some browsers report HEIC (and other files) with an empty type; the
  // server would refuse those, so say so now rather than after an upload.
  if (!ALLOWED_TYPES.includes(file.type)) return "Attach a JPG, PNG, HEIC or PDF file.";
  if (file.size > MAX_BYTES) return "Files can be up to 10 MB.";
  return null;
}

export async function uploadAttachment(file: File): Promise<AttachmentRef> {
  const ticket = await employeeApi.uploadUrl({ filename: file.name, mimeType: file.type, bytes: file.size });
  const res = await fetch(ticket.uploadUrl, { method: "PUT", headers: ticket.headers, body: file });
  if (!res.ok) throw new Error("Upload failed");
  return { key: ticket.key, filename: file.name, mimeType: file.type, bytes: file.size };
}
